import { useSettingsStore, type NotificationSettings } from '@/stores/settings'
import { firestoreService } from '@/utils/firestoreService'
import { useEmailService } from '@/services/emailService'
import { formatCurrency, formatWeight, capitalize } from '@/utils/formatters'

type NotificationChannel = 'email' | 'push' | 'sms'
type NotificationEventKey = 'transactionFinalized'

interface BaseNotificationPayload {
  event: NotificationEventKey
  channels: NotificationChannel[]
  createdAt: Date
  meta?: Record<string, unknown>
}

type TransactionFinalizedPayloadMeta = Record<string, unknown> & {
  transactionId: string
  transactionNumber?: string | number
  totalAmount?: number
  partyName?: string
}

export function useNotificationService() {
  const settingsStore = useSettingsStore()

  function getNotificationSettings(): NotificationSettings {
    // Ensure defaults are present even if settings haven't been loaded yet
    return settingsStore.notificationSettings as unknown as NotificationSettings
  }

  function isEventEnabled(event: NotificationEventKey): boolean {
    const notif = getNotificationSettings()
    return Boolean(notif.events?.[event])
  }

  function getActiveChannels(): NotificationChannel[] {
    const notif = getNotificationSettings()
    const channels: NotificationChannel[] = []
    if (notif.emailNotifications) channels.push('email')
    if (notif.pushNotifications) channels.push('push')
    if (notif.smsNotifications) channels.push('sms')
    return channels
  }

  async function recordNotification(
    payload: BaseNotificationPayload & { title: string; message: string },
  ) {
    // Persist a generic notification record. Delivery to channels can be expanded later.
    try {
      await firestoreService.add('notifications', {
        ...payload,
      })
    } catch (error) {
      console.error('Failed to record notification', error)
    }
  }

  return {
    isEventEnabled,
    getActiveChannels,
    /**
     * Notify that a transaction has been finalized
     */
    async notifyTransactionFinalized(meta: TransactionFinalizedPayloadMeta): Promise<void> {
      const notif = getNotificationSettings()

      if (!isEventEnabled('transactionFinalized')) return

      // Only act on immediate for now; daily/weekly batching can be added later
      if (notif.frequency !== 'immediate') return

      const channels = getActiveChannels()
      if (channels.length === 0) return

      await recordNotification({
        event: 'transactionFinalized',
        channels,
        createdAt: new Date(),
        title: 'Transaction finalized',
        message:
          typeof meta.transactionNumber !== 'undefined'
            ? `Transaction ${meta.transactionNumber} has been finalized.`
            : 'A transaction has been finalized.',
        meta,
      })

      // Channel deliveries
      if (notif.events.transactionFinalized.email && channels.includes('email')) {
        try {
          const emailService = useEmailService()
          const specific = notif.events.transactionFinalized.emailTo
          const fallback = notif.emailDefaultTo
          const recipient = specific || fallback
          if (recipient) {
            const { subject, text } = buildEmailForTransaction(meta)

            await emailService.sendEmail({
              to: recipient,
              message: { subject, text },
            } as any)
          }
        } catch (err) {
          console.warn('Failed to send notification email', err)
        }
      }
    },
  }
}

function buildEmailForTransaction(meta: Record<string, any>): { subject: string; text: string } {
  const transactionType = (meta.transactionType as string) || ''
  const typeLabel = transactionType
    ? transactionType === 'purchase'
      ? 'Purchase'
      : transactionType === 'sale'
        ? 'Sale'
        : capitalize(transactionType)
    : 'Transaction'

  const amount = typeof meta.totalAmount === 'number' ? meta.totalAmount : 0
  const amountStr = formatCurrency(amount)
  const subject = `New ${typeLabel} - ${amountStr}`

  const parts: string[] = []
  const numberStr = meta.transactionNumber ? `#${meta.transactionNumber}` : ''
  parts.push(`${typeLabel} ${numberStr}`.trim())
  parts.push(`Amount: ${amountStr}`)

  const personName = meta.personName || ''
  const organizationName = meta.organizationName || ''
  if (personName) parts.push(`Person: ${personName}`)
  if (organizationName) parts.push(`Company: ${organizationName}`)

  const items: any[] = Array.isArray(meta.items) ? meta.items : []
  if (items.length) {
    const totalNet = items.reduce((sum, it) => sum + (it?.weight?.net || 0), 0)
    parts.push(`Total Net: ${formatWeight(totalNet)}`)
    parts.push('Items:')
    for (const it of items) {
      const name = it.commodity_name || it.name || 'Item'
      const net = it?.weight?.net || 0
      const price = typeof it.price_per_lb === 'number' ? it.price_per_lb : 0
      const lineTotal = typeof it.total_price === 'number' ? it.total_price : price * net
      parts.push(
        ` - ${name}: ${formatWeight(net)} @ ${formatCurrency(price)} / lb = ${formatCurrency(lineTotal)}`,
      )
    }
  }

  const text = parts.join('\n')
  return { subject, text }
}
